import { useState, useEffect } from "react";
import { X, Trash2, Loader2, Plus, Phone, CalendarClock, Clock, CalendarDays, History, StickyNote } from "lucide-react";
import { STAGES, INDUSTRIES, COMPANY_SIZES, LEAD_SOURCES } from "../lib/constants";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import { CONSENT_BASIS } from "../lib/outreach";

const EMPTY = {
  company_name: "",
  full_name: "",
  first_name: "",
  last_name: "",
  contact_name: "",
  title: "",
  seniority: "",
  departments: "",
  email: "",
  secondary_email: "",
  phone: "",
  mobile_phone: "",
  work_direct_phone: "",
  corporate_phone: "",
  person_linkedin_url: "",
  contact_city: "",
  contact_state: "",
  contact_country: "",
  industry: "",
  company_size: "",
  num_employees: "",
  keywords: "",
  technologies: "",
  annual_revenue: "",
  website: "",
  company_linkedin_url: "",
  company_phone: "",
  company_address: "",
  company_street: "",
  company_city: "",
  company_state: "",
  company_country: "",
  company_postal_code: "",
  lead_source: "",
  next_follow_up_date: "",
  status: "New",
  estimated_deal_value: "",
  tags: [],
  salesperson: "",
  consent_basis: "",
  email_source_url: "",
  email_found_date: "",
  no_solicitation_checked: false,
};

const inputCls =
  "w-full bg-[#0B1120] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all";
const labelCls = "block text-xs font-medium text-slate-400 mb-1.5";

export default function LeadForm({ lead, mode = "modal", onSaved, onDeleted, onRefresh, onCancel }) {
  const [form, setForm] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [callLogs, setCallLogs] = useState([]);
  const [callNote, setCallNote] = useState("");
  const [callbackDate, setCallbackDate] = useState(null);
  const [callbackTime, setCallbackTime] = useState("09:00");
  const [logging, setLogging] = useState(false);
  const [salespeople, setSalespeople] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const isEdit = !!lead;
  const isPage = mode === "page";

  useEffect(() => {
    if (lead) {
      setForm({
        ...EMPTY,
        ...lead,
        next_follow_up_date: lead.next_follow_up_date || "",
        estimated_deal_value: lead.estimated_deal_value || "",
        tags: lead.tags || [],
        consent_basis: lead.consent_basis || "",
        email_source_url: lead.email_source_url || "",
        email_found_date: lead.email_found_date || "",
        no_solicitation_checked: !!lead.no_solicitation_checked,
      });
      setCallLogs(lead.call_logs || []);
      setNotes(lead.notes || []);
    } else {
      setForm(EMPTY);
      setCallLogs([]);
      setNotes([]);
    }
    setCallNote("");
    setNoteInput("");
    setCallbackDate(null);
    setCallbackTime("09:00");
  }, [lead]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/salespeople").then(({ data }) => setSalespeople(data)).catch(() => {});
  }, []);

  const addSalesperson = async () => {
    const name = window.prompt("Add a salesperson name:");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/salespeople", { name: name.trim() });
      setSalespeople((prev) =>
        prev.some((p) => p.id === data.id) ? prev : [...prev, data].sort((a, b) => a.name.localeCompare(b.name))
      );
      set("salesperson", data.name);
      toast.success(`Added ${data.name}`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not add salesperson");
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.company_name.trim()) {
      setError("Company name is required.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      estimated_deal_value: parseFloat(form.estimated_deal_value) || 0,
      next_follow_up_date: form.next_follow_up_date || null,
    };
    try {
      if (isEdit) {
        const { data } = await api.put(`/leads/${lead.id}`, payload);
        toast.success("Lead updated");
        onSaved && onSaved(data);
      } else {
        const { data } = await api.post("/leads", payload);
        toast.success("Lead created");
        onSaved && onSaved(data);
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    setSaving(true);
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      onDeleted && onDeleted(lead.id);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const addNote = async () => {
    if (!noteInput.trim()) return;
    setAddingNote(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/notes`, { text: noteInput.trim() });
      setNotes(data.notes || []);
      if (data.status) set("status", data.status);
      setNoteInput("");
      toast.success("Note added");
      onRefresh && onRefresh(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setAddingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const { data } = await api.delete(`/leads/${lead.id}/notes/${noteId}`);
      setNotes(data.notes || []);
      onRefresh && onRefresh(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const addCall = async () => {
    if (!callNote.trim()) return;
    setLogging(true);
    let callback_at = null;
    if (callbackDate) {
      const y = callbackDate.getFullYear();
      const m = String(callbackDate.getMonth() + 1).padStart(2, "0");
      const d = String(callbackDate.getDate()).padStart(2, "0");
      callback_at = `${y}-${m}-${d}T${callbackTime || "09:00"}`;
    }
    try {
      const { data } = await api.post(`/leads/${lead.id}/calls`, {
        note: callNote.trim(),
        callback_at,
      });
      setCallLogs(data.call_logs || []);
      if (data.status) set("status", data.status);
      setCallNote("");
      setCallbackDate(null);
      setCallbackTime("09:00");
      toast.success("Call logged");
      onRefresh && onRefresh(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLogging(false);
    }
  };

  const deleteCall = async (callId) => {
    try {
      const { data } = await api.delete(`/leads/${lead.id}/calls/${callId}`);
      setCallLogs(data.call_logs || []);
      onRefresh && onRefresh(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <form onSubmit={submit} className={isPage ? "space-y-5" : "p-6 space-y-4"}>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelCls}>Company Name *</label>
          <input data-testid="lead-company-input" className={inputCls} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Full Name</label>
          <input data-testid="lead-fullname-input" className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Contact Name</label>
          <input data-testid="lead-contact-input" className={inputCls} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Title</label>
          <input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Phone</label>
          <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Industry</label>
          <input data-testid="lead-industry-select" list="industry-options" className={inputCls} value={form.industry} onChange={(e) => set("industry", e.target.value)} placeholder="Select or type…" />
          <datalist id="industry-options">
            {INDUSTRIES.map((i) => <option key={i} value={i} />)}
          </datalist>
        </div>
        <div>
          <label className={labelCls}>Company Size (employees)</label>
          <input data-testid="lead-company-size-input" list="company-size-options" className={inputCls} value={form.company_size} onChange={(e) => set("company_size", e.target.value)} placeholder="e.g. 120 or 51-200" />
          <datalist id="company-size-options">
            {COMPANY_SIZES.map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        <div>
          <label className={labelCls}>Website</label>
          <input data-testid="lead-website-input" className={inputCls} value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" />
        </div>
        <div>
          <label className={labelCls}>Company LinkedIn URL</label>
          <input data-testid="lead-linkedin-input" className={inputCls} value={form.company_linkedin_url} onChange={(e) => set("company_linkedin_url", e.target.value)} placeholder="https://linkedin.com/company/…" />
        </div>
        <div>
          <label className={labelCls}>Company Phone</label>
          <input data-testid="lead-company-phone-input" className={inputCls} value={form.company_phone} onChange={(e) => set("company_phone", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Lead Source</label>
          <select className={inputCls} value={form.lead_source} onChange={(e) => set("lead_source", e.target.value)}>
            <option value="">Select…</option>
            {LEAD_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Salesperson</label>
          <div className="flex gap-2">
            <select
              data-testid="lead-salesperson-select"
              className={inputCls}
              value={form.salesperson}
              onChange={(e) => set("salesperson", e.target.value)}
            >
              <option value="">Unassigned</option>
              {salespeople.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              {form.salesperson && !salespeople.some((p) => p.name === form.salesperson) && (
                <option value={form.salesperson}>{form.salesperson}</option>
              )}
            </select>
            <button
              type="button"
              data-testid="add-salesperson-button"
              onClick={addSalesperson}
              title="Add a new salesperson"
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 hover:bg-slate-700 shrink-0"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div>
          <label className={labelCls}>Estimated Deal Value (CAD)</label>
          <input data-testid="lead-value-input" type="number" min="0" step="100" className={inputCls} value={form.estimated_deal_value} onChange={(e) => set("estimated_deal_value", e.target.value)} placeholder="0" />
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <select data-testid="lead-status-select" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
            {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2 pt-2">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 border-t border-slate-800 pt-3">Company Address</p>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Address</label>
          <input data-testid="lead-address-input" className={inputCls} value={form.company_address} onChange={(e) => set("company_address", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Street</label>
          <input className={inputCls} value={form.company_street} onChange={(e) => set("company_street", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>City</label>
          <input className={inputCls} value={form.company_city} onChange={(e) => set("company_city", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>State / Province</label>
          <input className={inputCls} value={form.company_state} onChange={(e) => set("company_state", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Country</label>
          <input className={inputCls} value={form.company_country} onChange={(e) => set("company_country", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Postal / Zip Code</label>
          <input className={inputCls} value={form.company_postal_code} onChange={(e) => set("company_postal_code", e.target.value)} />
        </div>
      </div>

      <div data-testid="lead-casl-section" className="border-t border-slate-800 pt-3">
        <p className="text-sm font-semibold text-slate-300 mb-3">CASL consent record</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Consent basis</label>
            <select data-testid="lead-consent-basis-select" className={inputCls} value={form.consent_basis} onChange={(e) => set("consent_basis", e.target.value)}>
              <option value="">Not recorded</option>
              <option value={CONSENT_BASIS}>{CONSENT_BASIS}</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Date email was found</label>
            <input data-testid="lead-email-found-date-input" type="date" className={inputCls} value={form.email_found_date} onChange={(e) => set("email_found_date", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Email source URL (public page where the address is published)</label>
            <input data-testid="lead-email-source-url-input" className={inputCls} value={form.email_source_url} onChange={(e) => set("email_source_url", e.target.value)} placeholder="https://company.com/contact" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-300">
            <input data-testid="lead-no-solicitation-checkbox" type="checkbox" className="accent-amber-500" checked={!!form.no_solicitation_checked} onChange={(e) => set("no_solicitation_checked", e.target.checked)} />
            No-solicitation statement checked
          </label>
        </div>
      </div>

      <details className="border-t border-slate-800 pt-3" data-testid="lead-more-details">
        <summary className="cursor-pointer text-sm font-semibold text-slate-300 hover:text-white">
          Additional details (phones, seniority, location, enrichment)
        </summary>
        <div className="grid sm:grid-cols-2 gap-4 mt-3">
          <div>
            <label className={labelCls}>Seniority</label>
            <input className={inputCls} value={form.seniority} onChange={(e) => set("seniority", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Departments</label>
            <input className={inputCls} value={form.departments} onChange={(e) => set("departments", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Mobile Phone</label>
            <input data-testid="lead-mobile-phone-input" className={inputCls} value={form.mobile_phone} onChange={(e) => set("mobile_phone", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Work Direct Phone</label>
            <input data-testid="lead-work-phone-input" className={inputCls} value={form.work_direct_phone} onChange={(e) => set("work_direct_phone", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Corporate Phone</label>
            <input className={inputCls} value={form.corporate_phone} onChange={(e) => set("corporate_phone", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Secondary Email</label>
            <input data-testid="lead-secondary-email-input" className={inputCls} value={form.secondary_email} onChange={(e) => set("secondary_email", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Person LinkedIn URL</label>
            <input data-testid="lead-person-linkedin-input" className={inputCls} value={form.person_linkedin_url} onChange={(e) => set("person_linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/…" />
          </div>
          <div>
            <label className={labelCls}>Contact City</label>
            <input className={inputCls} value={form.contact_city} onChange={(e) => set("contact_city", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Contact State/Province</label>
            <input className={inputCls} value={form.contact_state} onChange={(e) => set("contact_state", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Contact Country</label>
            <input className={inputCls} value={form.contact_country} onChange={(e) => set("contact_country", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Annual Revenue</label>
            <input className={inputCls} value={form.annual_revenue} onChange={(e) => set("annual_revenue", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Keywords</label>
            <input className={inputCls} value={form.keywords} onChange={(e) => set("keywords", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Technologies</label>
            <input className={inputCls} value={form.technologies} onChange={(e) => set("technologies", e.target.value)} />
          </div>
        </div>
      </details>

      <div>
        <label className={labelCls}>Tags</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {form.tags.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full px-2.5 py-0.5 text-xs">
              {t}
              <button type="button" onClick={() => set("tags", form.tags.filter((x) => x !== t))} className="hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            className={inputCls}
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
            placeholder="Add a tag and press Enter"
          />
          <button type="button" onClick={addTag} className="bg-slate-800 border border-slate-700 rounded-lg px-3 hover:bg-slate-700">
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isEdit && (
        <div className="border-t border-slate-800 pt-4" data-testid="notes-section">
          <div className="flex items-center gap-2 mb-3">
            <StickyNote className="w-4 h-4 text-amber-400" />
            <h3 className="font-display font-bold text-base text-white">Notes</h3>
            <span className="text-xs text-slate-500">({notes.filter((n) => !n.system).length})</span>
          </div>
          <div className="bg-[#0B1120] border border-slate-700 rounded-lg p-3 space-y-3">
            <textarea
              data-testid="note-input"
              rows={2}
              className={inputCls}
              placeholder="Add a note…"
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
            />
            <div className="flex justify-end">
              <button
                type="button"
                data-testid="add-note-button"
                onClick={addNote}
                disabled={addingNote || !noteInput.trim()}
                className="btn-primary rounded-lg py-2 px-4 text-sm inline-flex items-center gap-2"
              >
                {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add Note
              </button>
            </div>
          </div>
          <div className="mt-3 space-y-2 max-h-56 overflow-y-auto" data-testid="notes-list">
            {notes.length === 0 && <p className="text-xs text-slate-500 text-center py-3">No notes yet.</p>}
            {[...notes].reverse().map((n) =>
              n.system ? (
                <div key={n.id} data-testid={`note-${n.id}`} className="flex items-center gap-2 text-[11px] text-slate-500 px-2 py-1.5">
                  <History className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                  <span className="italic">{n.text}</span>
                  <span className="text-slate-600">· {fmtDateTime(n.created_at)}{n.author ? ` · ${n.author}` : ""}</span>
                </div>
              ) : (
                <div key={n.id} data-testid={`note-${n.id}`} className="bg-[#0B1120] border border-slate-800 rounded-lg p-3 group">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-slate-200 whitespace-pre-wrap flex-1">{n.text}</p>
                    <button type="button" data-testid={`delete-note-${n.id}`} onClick={() => deleteNote(n.id)} className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDateTime(n.created_at)}</span>
                    {n.author && <span>· {n.author}</span>}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {isEdit && (
        <div className="border-t border-slate-800 pt-4" data-testid="call-log-section">
          <div className="flex items-center gap-2 mb-3">
            <Phone className="w-4 h-4 text-amber-400" />
            <h3 className="font-display font-bold text-base text-white">Call Log</h3>
            <span className="text-xs text-slate-500">({callLogs.filter((c) => !c.system).length})</span>
          </div>

          <div className="bg-[#0B1120] border border-slate-700 rounded-lg p-3 space-y-3">
            <textarea
              data-testid="call-note-input"
              rows={2}
              className={inputCls}
              placeholder="What was discussed on the call?"
              value={callNote}
              onChange={(e) => setCallNote(e.target.value)}
            />
            <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1">
                <label className="block text-[11px] font-medium text-slate-400 mb-1 inline-flex items-center gap-1">
                  <CalendarClock className="w-3.5 h-3.5" /> Call back on (optional)
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="callback-date-trigger"
                      className={`${inputCls} text-left inline-flex items-center gap-2 ${callbackDate ? "text-white" : "text-slate-500"}`}
                    >
                      <CalendarDays className="w-4 h-4 text-amber-400 shrink-0" />
                      {callbackDate ? callbackDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "Pick a date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#131C31] border-slate-700" align="start">
                    <Calendar
                      mode="single"
                      selected={callbackDate}
                      onSelect={setCallbackDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="w-full sm:w-32">
                <label className="block text-[11px] font-medium text-slate-400 mb-1 inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> Time
                </label>
                <input
                  data-testid="callback-time-input"
                  type="time"
                  className={inputCls}
                  value={callbackTime}
                  onChange={(e) => setCallbackTime(e.target.value)}
                />
              </div>
              <button
                type="button"
                data-testid="log-call-button"
                onClick={addCall}
                disabled={logging || !callNote.trim()}
                className="btn-primary rounded-lg py-2 px-4 text-sm inline-flex items-center justify-center gap-2 shrink-0"
              >
                {logging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Log Call
              </button>
            </div>
          </div>

          <div className="mt-3 space-y-2 max-h-56 overflow-y-auto" data-testid="call-log-list">
            {callLogs.length === 0 && (
              <p className="text-xs text-slate-500 text-center py-3">No calls logged yet.</p>
            )}
            {[...callLogs].reverse().map((c) =>
              c.system ? (
                <div key={c.id} data-testid={`call-log-${c.id}`} className="flex items-center gap-2 text-[11px] text-slate-500 px-2 py-1.5">
                  <History className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                  <span className="italic">{c.note}</span>
                  <span className="text-slate-600">· {fmtDateTime(c.created_at)}{c.author ? ` · ${c.author}` : ""}</span>
                </div>
              ) : (
                <div key={c.id} data-testid={`call-log-${c.id}`} className="bg-[#0B1120] border border-slate-800 rounded-lg p-3 group">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-slate-200 whitespace-pre-wrap flex-1">{c.note}</p>
                    <button type="button" onClick={() => deleteCall(c.id)} className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDateTime(c.created_at)}</span>
                    {c.author && <span>· {c.author}</span>}
                    {c.callback_at && (
                      <span className="inline-flex items-center gap-1 text-amber-400"><CalendarClock className="w-3 h-3" /> Call back {fmtDateTime(c.callback_at)}</span>
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}

      <div className="flex items-center justify-between pt-2">
        {isEdit ? (
          <button data-testid="delete-lead-button" type="button" onClick={remove} disabled={saving} className="text-red-400 hover:text-red-300 text-sm inline-flex items-center gap-1.5">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        ) : <span />}
        <div className="flex gap-3">
          <button type="button" onClick={onCancel} className="bg-slate-800 text-white font-semibold py-2 px-4 rounded-lg border border-slate-700 hover:bg-slate-700 text-sm">
            {isPage ? "Back to Pipeline" : "Cancel"}
          </button>
          <button data-testid="lead-form-submit-button" type="submit" disabled={saving} className="btn-primary rounded-lg py-2 px-5 text-sm inline-flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Save Changes" : "Create Lead"}
          </button>
        </div>
      </div>
    </form>
  );
}
